// src/views/dashboard/ErrorLog.jsx

import React, { useEffect, useState, useCallback } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CircularProgress,
  MenuItem,
  Select,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { DateRangePicker } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';
import moment from 'moment';
import toast from 'react-hot-toast';
import { getDateRangeFromQuickOption } from 'utils/common';
import getAxiosInstance from 'api';
import ReactApexChart from 'react-apexcharts';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import LeftArrow from '../../assets/images/svg/left-arrow.svg';

const StyledTableCell = styled(TableCell)(() => ({
  backgroundColor: '#FFF',
  border: '1px solid #DEDEDE',
  color: '#212120',
  fontSize: 14,
  fontWeight: 500,
}));

const StyledTableCellHead = styled(TableCell)(() => ({
  backgroundColor: '#F0F0F0',
  color: 'rgba(33,33,32,0.6)',
  border: '1px solid #DEDEDE',
}));

const StyledCard = styled(Card)(() => ({
  padding: '24px',
  boxShadow: '0px 2px 6px rgba(0,0,0,0.06)',
  borderRadius: '12px',
  border: '1px solid rgba(0,0,0,0.1)',
  width: '505px',
}));

const quickRanges = [
  { label: 'Current Day', value: 'day' },
  { label: 'Current Week', value: 'week' },
  { label: 'Current Month', value: 'month' },
  { label: 'Last 3 Months', value: '3months' },
  { label: 'Last 6 Months', value: '6months' },
  { label: 'Last 12 Months', value: '12months' },
  { label: 'Quarter to date', value: 'quarter' },
  { label: 'All Time', value: 'all' },
];

const BackButton = styled(Button)(() => ({
  backgroundColor: '#fff',
  color: '#000',
  borderRadius: '50%',
  textTransform: 'none',
  fontWeight: 400,
  padding: '4px',
  minWidth: '32px',
  height: '32px',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
}));

const headCells = [
  { id: 'consentId', label: 'Consent ID' },
  { id: 'bankName', label: 'Bank Name' },
  { id: 'businessPartner', label: 'Business Partner' },
  { id: 'accountNumber', label: 'Account Number' },
  { id: 'journey', label: 'Journey' },
  { id: 'uploadType', label: 'Upload Type' },
  { id: 'createdAt', label: 'Created At' },
  { id: 'status', label: 'Status' },
];

export default function ErrorLog() {
  const theme = useTheme();
  const { tableName } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  // Filtering & sort state
  const [dateRange, setDateRange] = useState([
    moment().startOf('month').toDate(),
    moment().toDate(),
  ]);
  const [order, setOrder] = useState('DESC');
  const [orderBy, setOrderBy] = useState('createdAt');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [quickRangeValue, setQuickRangeValue] = useState('');
  const [selectedType, setSelectedType] = useState(
    state?.filterType || (tableName === 'api' ? 'API' : 'Online')
  );

  // Data state
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);

  // Pie chart state
  const [pieConfig, setPieConfig] = useState({
    series: [],
    options: {
      chart: { type: 'pie' },
      labels: [],
      legend: { position: 'bottom' },
      theme: {
        monochrome: {
          enabled: true,
          color: '#FF6543',
          shadeTo: 'light',
          shadeIntensity: 0.65,
        },
      },
      responsive: [
        {
          breakpoint: 768,
          options: { chart: { width: '100%' }, legend: { position: 'bottom' } },
        },
      ],
    },
  });
  const [loadingChart, setLoadingChart] = useState(false);
  const [partnerIds, setPartnerIds] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState(null);

  // Fetch logs data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        startDate: moment(dateRange[0]).format('YYYY-MM-DD'),
        endDate: moment(dateRange[1]).format('YYYY-MM-DD'),
        page: page + 1,
        limit,
        sortBy: orderBy,
        sortDirection: order,
        filter: {
          consentInitiator: selectedType || '',
          ...(selectedPartnerId !== null && {
            businessPartnerId: selectedPartnerId,
          }),
        },
      };

      const res = await getAxiosInstance(
        'adminOrchestrator/app/getConsentDashboardData',
        'post',
        payload,
        { 'Content-Type': 'application/json' }
      );

      if (res.data.codes === 200 && res.data.data) {
        setRows(res.data.data.items || []);
        setTotalRows(res.data.data.total || 0);
      } else {
        toast.error(res.data.message || 'Failed to load logs');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [dateRange, page, limit, order, orderBy, selectedType, selectedPartnerId]);

  // Fetch pie chart data
  const fetchPieChartData = useCallback(async () => {
    setLoadingChart(true);
    try {
      const payload = {
        startDate: moment(dateRange[0]).format('YYYY-MM-DD'),
        endDate: moment(dateRange[1]).format('YYYY-MM-DD'),
        filter: {
          consentInitiator: selectedType || '',
        },
      };

      const res = await getAxiosInstance(
        'adminOrchestrator/app/getConsentErrorCountsByPartner',
        'post',
        payload,
        { 'Content-Type': 'application/json' }
      );

      if (res?.data?.codes === 200) {
        const responseData = res.data.data || [];
        if (responseData.length === 0) {
          setPartnerIds([]);
          setPieConfig({
            series: [0],
            options: { ...pieConfig.options, labels: ['No Data'] },
          });
          return;
        }

        const labels = responseData.map(
          (item) => item.businessName || 'Unknown'
        );
        const series = responseData.map((item) => item.errorCount);
        const ids = responseData.map((item) => item.businessPartnerId);

        setPartnerIds(ids);
        setPieConfig({
          series,
          options: {
            ...pieConfig.options,
            labels,
            chart: {
              type: 'pie',
              events: {
                dataPointSelection: (event, chartContext, config) => {
                  const clickedIndex = config.dataPointIndex;
                  const partnerId = ids[clickedIndex];
                  if (partnerId) {
                    setSelectedPartnerId(partnerId);
                    setPage(0);
                  }
                },
              },
            },
          },
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to fetch chart data');
    } finally {
      setLoadingChart(false);
    }
  }, [dateRange, selectedType]);

  // Effects
  useEffect(() => {
    fetchPieChartData();
  }, [fetchPieChartData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRequestSort = (_, property) => {
    const isAsc = orderBy === property && order === 'ASC';
    setOrder(isAsc ? 'DESC' : 'ASC');
    setOrderBy(property);
  };

  return (
    <Box>
      {/* Header */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <BackButton onClick={() => navigate(-1)}>
            <Box component={'img'} src={LeftArrow} />
          </BackButton>
          <Typography variant="h4">{state?.tableName}</Typography>
        </Box>
        {selectedPartnerId !== null && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setSelectedPartnerId(null)}
          >
            Reset Partner Filter
          </Button>
        )}
      </Stack>

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="flex-end"
        flexWrap="wrap"
        rowGap={1.5}
        mb={2}
      >

        <Select
          size="small"
          value={quickRangeValue}
          onChange={(event) => {
            const newValue = event.target.value;
            setQuickRangeValue(newValue);
            if (!newValue) return;
            const range = getDateRangeFromQuickOption(newValue);
            if (!range) return;
            setDateRange([range.start, range.end]);
            setPage(0);
          }}
          displayEmpty
          sx={{ minWidth: 170, backgroundColor: '#fff' }}
        >
          <MenuItem value="">Quick Range</MenuItem>
          {quickRanges.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>

        <DateRangePicker
          appearance="default"
          placeholder="Select Date Range"
          value={dateRange}
          onChange={(vals) => {
            if (!vals || vals.length < 2) return;
            setDateRange([vals[0], vals[1]]);
            setQuickRangeValue('');
            setPage(0);
          }}
          showOneCalendar
        />
      </Stack>

      {/* Pie Chart */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <StyledCard>
          {loadingChart ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                height: '300px',
                alignItems: 'center',
              }}
            >
              <CircularProgress color="secondary" />
            </Box>
          ) : (
            <ReactApexChart
              options={pieConfig.options}
              series={pieConfig.series}
              type="pie"
              height={300}
            />
          )}
        </StyledCard>
      </Box>

      {/* Table */}
      <Card>
        {loading ? (
          <Box p={3} display="flex" justifyContent="center">
            <CircularProgress color="secondary" />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {headCells.map((cell) => (
                      <StyledTableCellHead
                        key={cell.id}
                        sortDirection={orderBy === cell.id ? order : false}
                      >
                        <TableSortLabel
                          active={orderBy === cell.id}
                          direction={
                            orderBy === cell.id ? order.toLowerCase() : 'asc'
                          }
                          onClick={(e) => handleRequestSort(e, cell.id)}
                        >
                          {cell.label}
                          {orderBy === cell.id && (
                            <Box component="span" sx={visuallyHidden}>
                              {order === 'DESC'
                                ? 'sorted descending'
                                : 'sorted ascending'}
                            </Box>
                          )}
                        </TableSortLabel>
                      </StyledTableCellHead>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.length > 0 ? (
                    rows.map((row, idx) => (
                      <TableRow hover key={idx}>
                        <StyledTableCell>{row.consentId}</StyledTableCell>
                        <StyledTableCell>{row.bankName}</StyledTableCell>
                        <StyledTableCell>{row.businessName}</StyledTableCell>
                        <StyledTableCell>
                          {row.accountNumber?.replace(/.(?=.{4})/g, 'x')}
                        </StyledTableCell>
                        <StyledTableCell>{row.requestType}</StyledTableCell>
                        <StyledTableCell>{row.uploadType}</StyledTableCell>
                        <StyledTableCell>
                          {moment(row.createdAt).format('DD MMM YYYY, HH:mm')}
                        </StyledTableCell>
                        <StyledTableCell>{row.status}</StyledTableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={headCells.length} align="center">
                        No records found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalRows}
              page={page}
              rowsPerPage={limit}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setLimit(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </>
        )}
      </Card>
    </Box>
  );
}
